import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Loader2,
  LogOut,
  X,
  CheckCircle2,
  AlertCircle,
  Bus,
  UtensilsCrossed,
  LogIn,
  Edit,
  Flag,
  Shield,
  ShieldCheck,
  ShieldX,
  ShieldAlert,
  Package,
  Phone
} from "lucide-react";
import AttendanceFile from "./AttendanceFile.jsx";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import CalendarInput from "./calendarInput";
import Pagination from "./Common/Pagination";
import defaultProfile from "../assets/profile.png"

// ── Separated sub-components ──────────────────────────────────────────────────
import HandleEntry from "./Common/HandleEntry.jsx";
import HandleExit from "./Common/HandleExit.jsx";
import HandlePayment from "./Common/HandlePayment.jsx";

// ── Outside-click hook ────────────────────────────────────────────────────────
const useOutsideClick = (callback) => {
  const ref = useRef();
  useEffect(() => {
    const handleClick = (event) => {
      if (ref.current && !ref.current.contains(event.target)) callback();
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("touchstart", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("touchstart", handleClick);
    };
  }, [callback]);
  return ref;
};
const ViewAttendance = () => {
  console.log("ViewAttendance component mounted");
  const navigate = useNavigate();
  const [isInitializing, setIsInitializing] = useState(true);

  // ── Location / Company ────────────────────────────────────────────────────────
  const [locations, setLocations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [isCompanyOpen, setIsCompanyOpen] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [companyid, setcompanyid] = useState("");

  // ── Filters ───────────────────────────────────────────────────────────────────
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [dateError, setDateError] = useState("");
  const [fetchError, setFetchError] = useState("");

  // ── Table data ────────────────────────────────────────────────────────────────
  const [attendanceData, setAttendanceData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [showTable, setShowTable] = useState(false);
  const [imageLoadingStates, setImageLoadingStates] = useState({});

  // ── Photo modal ───────────────────────────────────────────────────────────────
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState("");
  const [isModalImageLoading, setIsModalImageLoading] = useState(false);

  // ── Entry / Exit modal triggers ───────────────────────────────────────────────
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showentrymarkdays, setshowentrymarkdays] = useState("")

  // ── Payment ───────────────────────────────────────────────────────────────────
  const [paymentEditModal, setPaymentEditModal] = useState({ isOpen: false, record: null });
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [selectedAttendanceId, setSelectedAttendanceId] = useState(null);
  const [retryingPayments, setRetryingPayments] = useState({});

  // ── Worker-accepted tooltip ───────────────────────────────────────────────────
  const [workerAcceptedTooltip, setWorkerAcceptedTooltip] = useState({
    visible: false, content: "", position: { x: 0, y: 0 }, recordId: null,
  });

  // ── Payment reference tooltip ─────────────────────────────────────────────────
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipContent, setTooltipContent] = useState(null);
  const [activeTooltipId, setActiveTooltipId] = useState(null);
  const [isTooltipPinned, setIsTooltipPinned] = useState(false);

  // ── Download ──────────────────────────────────────────────────────────────────
  const [attendanceDownloadLoading, setAttendanceDownloadLoading] = useState(false);

  // ── User / auth ───────────────────────────────────────────────────────────────
  const [isUTVMember, setIsUTVMember] = useState(false);
  const [featureAccess, setFeatureAccess] = useState([]);
  const [userRole, setUserRole] = useState("");
  const isUTV = isUTVMember ? "true" : "false";

  // ── Departments / payment filter ──────────────────────────────────────────────
  const [departments, setDepartments] = useState([]);
  const [allDepartments, setAllDepartments] = useState([]); // full department objects
  const [alldepartmentnames, setalldepartmentnames] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState([]);
  const [isDeptOpen11, setIsDeptOpen11] = useState(false);
  const [isDeptOpen12, setIsDeptOpen12] = useState(false);
  const [selectedPaymentStatuses, setSelectedPaymentStatuses] = useState([]);
  const [isPaymentOpen11, setIsPaymentOpen11] = useState(false);
  const [isPaymentOpen12, setIsPaymentOpen12] = useState(false);
  const paymentOptions = ["Pending", "Success", "Payout Generated", "Failed"];
  const paymentStatusMap = {
    Pending: "pending", Success: "success",
    "Payout Generated": "payout_generated", Failed: "failed",
  };

  // ── Attendance file upload ────────────────────────────────────────────────────
  const [attendancefilepage, setAttendanceFilePage] = useState(false);
  const [showuploadfilemodal, setshowuploadfilemodal] = useState(true);
  const [Success, setSuccess] = useState(false);

  // ── Calendar open state ───────────────────────────────────────────────────────
  const [isCalendarOpen, setIsCalendarOpen] = useState({
    fromDate: false, toDate: false,
  });

  const isInitialMount = useRef(true);
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  // ── Reset table state when location or company changes ────────────────────────
  useEffect(() => {
    setShowTable(false);
    setAttendanceData([]);
    setCurrentPage(1);
    setTotalPages(1);
    setTotalItems(0);
    setFromDate("");
    setToDate("");
    setSelectedDepartments([]);
    setSelectedDepartmentIds([]);
    setSelectedPaymentStatuses([]);
    setFetchError("");
    setDateError("");
  }, [selectedLocation, selectedCompany]);

  // ── Calendar toggle ───────────────────────────────────────────────────────────
  const toggleCalendar = (field, e) => {
    e.preventDefault();
    if (isCalendarOpen[field]) {
      e.target.blur();
      setIsCalendarOpen((prev) => ({ ...prev, [field]: false }));
    } else {
      e.target.showPicker();
      setIsCalendarOpen((prev) => ({ ...prev, [field]: true }));
    }
  };

  const handleCalendarBlur = (field) => {
    setTimeout(() => setIsCalendarOpen((prev) => ({ ...prev, [field]: false })), 200);
  };

  // ── WorkerAccepted tooltip ────────────────────────────────────────────────────
  const showWorkerAcceptedTooltip = (e, record) => {
    const rect = e.target.getBoundingClientRect();
    let content = "";
    if (record.WorkerAccepted === "pending") content = "Action pending";
    else if (record.WorkerAccepted === "rejected") content = record.rejectionReason || "Rejected";
    setWorkerAcceptedTooltip({
      visible: true, content,
      position: { x: rect.left + rect.width / 2, y: rect.top - 10 },
      recordId: record.attendanceId,
    });
  };

  const hideWorkerAcceptedTooltip = () => {
    setWorkerAcceptedTooltip({ visible: false, content: "", position: { x: 0, y: 0 }, recordId: null });
  };

  // ── Payment ref tooltip ───────────────────────────────────────────────────────
  const showTooltip = (e, record, isPinned = false) => {
    const rect = e.target.closest(".tooltip-trigger").getBoundingClientRect();
    setTooltipPosition({ x: rect.left + rect.width / 2 - 25, y: rect.top - 10 });
    setTooltipContent({
      referenceId: record.payment?.referenceId || "N/A",
      remarks: record.payment?.remark || record.payment?.transactionRemarks || "N/A",
    });
    setTooltipVisible(true);
    setActiveTooltipId(record.attendanceId);
    setIsTooltipPinned(isPinned);
  };

  const hideTooltip = () => {
    if (!isTooltipPinned) {
      setTooltipVisible(false);
      setTooltipContent(null);
      setActiveTooltipId(null);
    }
  };

  // ── Fetch locations ───────────────────────────────────────────────────────────
  const fetchLocations = async () => {
    try {
      const token = localStorage.getItem("superadmintoken");
      const response = await axios.get(`${BASE_URL}/lenskart/locations`, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (response.data.success && Array.isArray(response.data.locations)) {
        setLocations(response.data.locations);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error fetching locations:", error);
      return false;
    }
  };

  // ── Fetch companies when location changes ─────────────────────────────────────
  useEffect(() => {
    const fetchCompanies = async () => {
      if (!selectedLocation) { setCompanies([]); return; }
      try {
        setLoadingCompanies(true);
        const token = localStorage.getItem("superadmintoken");
        const response = await axios.get(
          `${BASE_URL}/onDemandCompanies/list-companies/${selectedLocation}`,
          { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
        );
        if (response.data && Array.isArray(response.data.companies)) {
          setCompanies(response.data.companies);
        } else {
          setCompanies([]);
        }
      } catch { setCompanies([]); }
      finally { setLoadingCompanies(false); }
    };
    fetchCompanies();
  }, [selectedLocation, BASE_URL]);

  // Reset company when location changes
  useEffect(() => { setSelectedCompany(""); }, [selectedLocation]);

  // ── Dropdown refs ─────────────────────────────────────────────────────────────
  const locationDropdownRef = useOutsideClick(() => { if (isLocationOpen) setIsLocationOpen(false); });
  const companyDropdownRef = useOutsideClick(() => { if (isCompanyOpen) setIsCompanyOpen(false); });
  const departmentDropdownRef1 = useOutsideClick(() => { if (isDeptOpen11) setIsDeptOpen11(false); });
  const departmentDropdownRef2 = useOutsideClick(() => { if (isDeptOpen12) setIsDeptOpen12(false); });
  const paymentDropdownRef1 = useOutsideClick(() => { if (isPaymentOpen11) setIsPaymentOpen11(false); });
  const paymentDropdownRef2 = useOutsideClick(() => { if (isPaymentOpen12) setIsPaymentOpen12(false); });

  const toggleDepartmentDropdown1 = () => { setIsPaymentOpen11(false); setIsDeptOpen11((p) => !p); };
  const toggleDepartmentDropdown2 = () => { setIsPaymentOpen12(false); setIsDeptOpen12((p) => !p); };
  const togglePaymentDropdown1 = () => { setIsDeptOpen11(false); setIsPaymentOpen11((p) => !p); };
  const togglePaymentDropdown2 = () => { setIsDeptOpen12(false); setIsPaymentOpen12((p) => !p); };

  const [accessoryTooltip, setAccessoryTooltip] = useState({
  visible: false,
  content: null,
  position: { x: 0, y: 0 },
});

const showAccessoryTooltip = (e, record) => {
  const rect = e.currentTarget.getBoundingClientRect();
  setAccessoryTooltip({
    visible: true,
    content: {
      accessories: record.payment?.accessories || [],
      totalDeduction: record.ApronDeduction ?? record.totalDeductions ?? 0,
      totalPromotion : record?.payment?.bonusAmount || ""
    },
    position: { x: rect.left + rect.width / 2, y: rect.top },
  });
};

const hideAccessoryTooltip = () => {
  setAccessoryTooltip({ visible: false, content: null, position: { x: 0, y: 0 } });
};

  // ── Token verification ────────────────────────────────────────────────────────
  // FIX: use currentLocationId (not CompanyLocation) to match original code
  const verifyTokenWithAuth = useCallback(async (authToken) => {
    try {
      const response = await axios.get(`${BASE_URL}/lenskart/verify-token`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (response.data?.error?.name === "TokenExpiredError") {
        localStorage.removeItem("superadmintoken");
        return false;
      }
      if (response.data?.success && response.data?.user) {
        const userData = response.data.user;
        setIsUTVMember(userData.UTVMember || false);
        setFeatureAccess(userData.featureAccess || []);
        setUserRole(userData.role || "");

        // Only pre-select location/company for non-UTV members
        if (!userData.UTVMember) {
          if (userData.CompanyLocation) {
            setSelectedLocation(userData.currentLocationId); // use currentLocationId (the _id)
          }
          if (userData.MyCompany) {
            setSelectedCompany(userData.MyCompany);
          }
        }

        console.log("User data loaded from API:", {
          UTVMember: userData.UTVMember,
          location: userData.CompanyLocation,
          company: userData.MyCompany,
          role: userData.role,
        });
        return true;
      }
      return false;
    } catch {
      localStorage.removeItem("superadmintoken");
      return false;
    }
  }, [BASE_URL]);

  const hasFeatureAccess = (featureName) => {
    if (userRole === "super_admin") {
      if (featureName === "Upload CSV") return isUTVMember;
      return true;
    }
    if (userRole === "Admin") return featureAccess.includes(featureName);
    return false;
  };

  // ── Init ──────────────────────────────────────────────────────────────────────
  const initializeApp = useCallback(async () => {
    try {
      setIsInitializing(true);
      const token = localStorage.getItem("superadmintoken");
      if (!token) { navigate("/lkart/superadmin/"); return; }
      const isTokenValid = await verifyTokenWithAuth(token);
      if (!isTokenValid) { navigate("/lkart/superadmin/"); return; }
      await fetchLocations();
      if (isInitialMount.current) isInitialMount.current = false;
    } catch (error) {
      console.error("Initialization error:", error);
    } finally {
      setIsInitializing(false);
    }
  }, [navigate, verifyTokenWithAuth]);

  useEffect(() => { initializeApp(); }, [initializeApp]);

  useEffect(() => {
    const checkTokenValidity = async () => {
      const token = localStorage.getItem("superadmintoken");
      if (!token) { navigate("/lkart/superadmin/"); return; }
      const isTokenValid = await verifyTokenWithAuth(token);
      if (!isTokenValid) navigate("/lkart/superadmin/");
    };
    checkTokenValidity();
  }, [navigate, verifyTokenWithAuth]);

  // ── Fetch departments ─────────────────────────────────────────────────────────
  // FIX: also store full department objects in allDepartments for ID lookup
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const token = localStorage.getItem("superadmintoken");
        const response = await axios.post(
          `${BASE_URL}/onDemandCompanies/list-departments`,
          { companyId: selectedCompany },
          { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
        );
        if (response.data.success && Array.isArray(response.data.departments)) {
          const departmentNames = response.data.departments
            .map((d) => d.department)
            .filter((d) => d && d !== "All");
          setDepartments(departmentNames);
          setalldepartmentnames(departmentNames);
          setAllDepartments(response.data.departments); // store full objects for ID lookup
        } else {
          setDepartments([]);
          setalldepartmentnames([]);
          setAllDepartments([]);
        }
      } catch {
        setDepartments([]);
        setalldepartmentnames([]);
        setAllDepartments([]);
      }
    };
    if (selectedCompany) fetchDepartments();
    else {
      setDepartments([]);
      setalldepartmentnames([]);
      setAllDepartments([]);
      setSelectedDepartments([]);
      setSelectedDepartmentIds([]);
    }
  }, [BASE_URL, selectedCompany]);

  // ── Department / payment filter helpers ──────────────────────────────────────
  // FIX: also manage selectedDepartmentIds alongside selectedDepartments
  const toggleDepartment = (dept) => {
    const deptObj = allDepartments?.find((d) => d.department === dept);
    setSelectedDepartments((prev) =>
      prev.includes(dept) ? prev.filter((d) => d !== dept) : [...prev, dept]
    );
    setSelectedDepartmentIds((prev) => {
      const id = deptObj?._id;
      if (!id) return prev;
      return prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id];
    });
  };

  // FIX: also reset/set selectedDepartmentIds on select-all
  const handleSelectAllDepartments = () => {
    if (selectedDepartments.length === alldepartmentnames.length) {
      setSelectedDepartments([]);
      setSelectedDepartmentIds([]);
    } else {
      setSelectedDepartments([...alldepartmentnames]);
      setSelectedDepartmentIds(allDepartments?.map((d) => d._id).filter(Boolean) || []);
    }
  };

  const togglePaymentStatus = (status) => {
    setSelectedPaymentStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };
  const handleSelectAllPayments = () => {
    setSelectedPaymentStatuses(
      selectedPaymentStatuses.length === paymentOptions.length ? [] : [...paymentOptions]
    );
  };

  // ── Fetch attendance data ─────────────────────────────────────────────────────
  // FIX: also send departmentId param (selectedDepartmentIds)
const fetchAttendanceData = async (pageOrData, isNewSearch = false) => {
  const page = typeof pageOrData === "object" ? currentPage : pageOrData;
    try {
      setLoading(true);
      setFetchError("");
      const token = localStorage.getItem("superadmintoken");
      
      const params = { page, startDate: fromDate, endDate: toDate };
      if (selectedCompany) params.companyId = selectedCompany;
      if (selectedDepartments.length > 0) params.department = selectedDepartments.join(",");
      if (selectedDepartmentIds.length > 0) params.departmentId = selectedDepartmentIds.join(",");
      if (selectedPaymentStatuses.length > 0)
        params.payment = selectedPaymentStatuses.map((s) => paymentStatusMap[s]).join(",");

      const response = await axios.get(`${BASE_URL}/lenskart-admin/attendance/history`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = response.data;
      if (result.success) {
        setAttendanceData(result.data);
        setShowTable(true);
        setTotalPages(result.pagination.totalPages || 1);
        setTotalItems(result.pagination.totalItems || result.data.length);
        if (isNewSearch) setCurrentPage(1);
        setImageLoadingStates((prev) => {
          const newLoadingStates = {};
          result.data.forEach((record) => {
            if (record.profilePhotoUrl) {
              newLoadingStates[record.profilePhotoUrl] = prev[record.profilePhotoUrl] ?? true;
            }
          });
          return newLoadingStates;
        });
      } else {
        setFetchError(result.message || "Failed to fetch attendance data");
        setAttendanceData([]);
        setShowTable(false);
        setTotalPages(1);
        setTotalItems(0);
      }
    } catch (error) {
      setFetchError(error.response?.data?.message || "Error fetching attendance data. Please try again.");
      setAttendanceData([]);
      setShowTable(false);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  // ── Download attendance ───────────────────────────────────────────────────────
  // FIX: also send departmentId param (selectedDepartmentIds)
  const downloadAttendanceData = async () => {
    try {
      setAttendanceDownloadLoading(true);
      const token = localStorage.getItem("superadmintoken");
      let companyIdToSend = selectedCompany;
      let companyNameToSend = "";

      if (companyIdToSend) {
        try {
          const deptResponse = await axios.post(
            `${BASE_URL}/onDemandCompanies/list-departments`,
            { companyId: companyIdToSend },
            { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
          );
          if (deptResponse.data?.company) companyNameToSend = deptResponse.data.company.name;
        } catch { console.error("Error fetching company name"); }
      }

      const params = { startDate: fromDate, endDate: toDate };
      if (companyIdToSend) { params.companyId = companyIdToSend; setcompanyid(companyIdToSend); }
      if (companyNameToSend) params.companyName = companyNameToSend;
      if (selectedDepartments.length > 0) params.department = selectedDepartments.join(",");
      if (selectedDepartmentIds.length > 0) params.departmentId = selectedDepartmentIds.join(",");
      if (selectedPaymentStatuses.length > 0)
        params.payment = selectedPaymentStatuses.map((s) => paymentStatusMap[s]).join(",");

      const response = await axios.get(`${BASE_URL}/lenskart-admin/attendance-history/download`, {
        params, headers: { Authorization: `Bearer ${token}` }, responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `attendance-history-${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading:", error);
    } finally {
      setAttendanceDownloadLoading(false);
    }
  };

  // ── Row / modal open handlers ─────────────────────────────────────────────────
  const handleRowClick = (userId) => {
    if (activeTooltipId) {
      setActiveTooltipId(null);
      setTooltipVisible(false);
      setTooltipContent(null);
      setIsTooltipPinned(false);
      return;
    }
    const stateToSave = { fromDate, toDate, attendanceData, currentPage, showTable, selectedLocation, selectedCompany };
    localStorage.setItem("attendanceFilterState", JSON.stringify(stateToSave));
    window.open(`/lkart/superadmin/attendance-history/${userId}`, "_blank");
  };

  const handleEntryClick = async (record, e) => {
    e.stopPropagation();
    try {
      console.log(record);
      setLoading(true);
      const token = localStorage.getItem("superadmintoken");
      const response = await axios.post(
        `${BASE_URL}/lenskart-admin/worker-data`,
        { attendanceId: record.attendanceId, workerId: record.userId },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      if (response?.data?.success) {
        setSelectedRecord(response?.data);
        setShowEntryModal(true);
        setshowentrymarkdays(response?.data?.EntryMarkedForPreviousDays || "7")
      }
    } catch (error) {
      console.error("Error preparing entry edit:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExitClick = async (record, e) => {
    e.stopPropagation();
    try {
      console.log(record);
      setLoading(true);
      const token = localStorage.getItem("superadmintoken");
      const response = await axios.post(
        `${BASE_URL}/lenskart-admin/worker-data`,
        { attendanceId: record.attendanceId, workerId: record.userId },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      if (response?.data?.success) {
        setSelectedRecord(response?.data);
        setShowExitModal(true);
      }
    } catch (error) {
      console.error("Error preparing exit edit:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentEditClick = async (record, e) => {
    e.stopPropagation();
    try {
      console.log(record);
      setLoading(true);
      const token = localStorage.getItem("superadmintoken");
      const response = await axios.post(
        `${BASE_URL}/lenskart-admin/worker-data`,
        { attendanceId: record.attendanceId, workerId: record.userId },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      if (response?.data?.success) {
        setPaymentEditModal({ isOpen: true, record: response.data });
      }
    } catch (error) {
      console.error("Error payment handling:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePending = (attendanceId, e) => {
    e.stopPropagation();
    setSelectedAttendanceId(attendanceId);
    setShowConfirmationModal(true);
  };

  // ── Validate dates ────────────────────────────────────────────────────────────
  const validateDates = () => {
    if (fromDate && toDate) {
      if (new Date(fromDate) > new Date(toDate)) {
        setDateError("From date cannot be later than To date");
        return false;
      }
      setDateError("");
      return true;
    }
    return false;
  };

  const handleApply = () => {
    if (validateDates()) { setFetchError(""); fetchAttendanceData(1, true); }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchAttendanceData(page, false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ── Formatters ────────────────────────────────────────────────────────────────
  const formatDateToDisplay = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  const formatTimeToIST = (isoString) => {
    if (!isoString) return "-";
    const timePart = isoString.split("T")[1].split(".")[0];
    const [hours, minutes] = timePart.split(":");
    let hour = parseInt(hours);
    const period = hour >= 12 ? "PM" : "AM";
    if (hour > 12) hour -= 12;
    else if (hour === 0) hour = 12;
    return `${hour}:${minutes} ${period}`;
  };

  // Function to get account verification icon and tooltip
const getAccountVerificationIcon = (status) => {
  if (status === "success") {
    return {
      icon: <ShieldCheck className="h-4 w-4 text-green-600" />,
      tooltip: "Account Verified",
      color: "text-green-600",
    };
  }

  if (status === "failed") {
    return {
      icon: <ShieldX className="h-4 w-4 text-red-600" />,
      tooltip: "Account Not Verified",
      color: "text-red-600",
    };
  }

  return {
    icon: <Shield className="h-4 w-4 text-gray-400" />,
    tooltip: "Account Not Checked",
    color: "text-gray-400",
  };
};

  const getPaymentStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "success": return "bg-green-100 text-green-800";
      case "pending": return "bg-yellow-100 text-yellow-800";
      case "payoutgenerated": return "bg-blue-100 text-blue-800";
      case "failed": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const handleImageLoad = (photoUrl) => {
    setImageLoadingStates((prev) => ({ ...prev, [photoUrl]: false }));
  };

  const openPhotoModal = (photoUrl) => {
    setSelectedPhoto(photoUrl);
    setIsModalImageLoading(true);
    setShowPhotoModal(true);
  };

  const closePhotoModal = () => {
    setShowPhotoModal(false);
    setSelectedPhoto("");
    setIsModalImageLoading(false);
  };

  // ── Loading screen ────────────────────────────────────────────────────────────
  if (isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="flex flex-col items-center">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600 mb-2" />
          <p className="text-gray-600">Loading attendance data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-y-auto pt-16 bg-gray-100 p-0 z-2">
      <div className="max-w-6xl mx-auto bg-white shadow-lg rounded-lg p-4 overflow-visible">
        <h2 className="text-2xl font-bold mb-4 text-center">View Attendance</h2>

        {/* ── Location & Company ── */}
        <div className="mb-6">
          <div className="hidden md:flex gap-4 mb-4">
            {/* Location */}
            <div className="flex-1 relative">
              <label className="text-sm text-gray-600 mb-1 block">
                Location <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                {isUTVMember ? (
                  <>
                    <button
                      onClick={() => setIsLocationOpen(!isLocationOpen)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md text-left flex justify-between items-center bg-white"
                    >
                      {/* FIX: resolve name from locations array using _id */}
                      {locations.find((l) => l._id === selectedLocation)?.name || "Select Location"}
                      <svg className={`h-4 w-4 transform transition-transform ${isLocationOpen ? "rotate-180" : ""}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                    {isLocationOpen && (
                      <div className="absolute z-50 mt-1 w-full bg-white shadow-lg rounded-md py-1 max-h-48 overflow-auto border border-gray-300">
                        {locations.map((location, index) => (
                          <button
                            key={index}
                            onClick={() => { setSelectedLocation(location._id); setIsLocationOpen(false); }}
                            className={`block w-full text-left px-4 py-2 text-sm hover:bg-indigo-100 ${selectedLocation === location._id ? "bg-indigo-50 text-indigo-700" : "text-gray-700"}`}
                          >
                            {location.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  /* FIX: resolve name from locations array for read-only display */
                  <input
                    type="text"
                    readOnly
                    value={
                      locations.find((l) => l._id === selectedLocation)?.name ||
                      locations.find((l) => l.id === selectedLocation)?.name ||
                      selectedLocation || ""
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 text-gray-700 cursor-not-allowed"
                  />
                )}
              </div>
            </div>

            {/* Company */}
            <div className="flex-1 relative">
              <label className="text-sm text-gray-600 mb-1 block">
                Company <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                {isUTVMember ? (
                  <>
                    <button
                      onClick={() => { if (selectedLocation && !loadingCompanies) setIsCompanyOpen(!isCompanyOpen); }}
                      disabled={!selectedLocation || loadingCompanies}
                      className={`w-full px-3 py-2 text-sm border border-gray-300 rounded-md text-left flex justify-between items-center ${!selectedLocation || loadingCompanies ? "bg-gray-100 cursor-not-allowed text-gray-400" : "bg-white"}`}
                    >
                      {loadingCompanies
                        ? <span className="flex items-center"><Loader2 className="h-3 w-3 animate-spin mr-2" />Loading...</span>
                        : selectedCompany
                          ? companies.find((c) => c._id === selectedCompany)?.name || "Select Company"
                          : "Select Company"}
                      <svg className={`h-4 w-4 transform transition-transform ${isCompanyOpen ? "rotate-180" : ""} ${!selectedLocation || loadingCompanies ? "text-gray-400" : ""}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                    {isCompanyOpen && selectedLocation && !loadingCompanies && (
                      <div className="absolute z-50 mt-1 w-full bg-white shadow-lg rounded-md py-1 max-h-48 overflow-auto border border-gray-300">
                        {companies.length > 0 ? companies.map((company) => (
                          <button
                            key={company._id}
                            onClick={() => { setSelectedCompany(company._id); setIsCompanyOpen(false); }}
                            className={`block w-full text-left px-4 py-2 text-sm hover:bg-indigo-100 ${selectedCompany === company._id ? "bg-indigo-50 text-indigo-700" : "text-gray-700"}`}
                          >
                            {company.name}
                          </button>
                        )) : <div className="px-4 py-2 text-sm text-gray-500">No companies found for this location</div>}
                      </div>
                    )}
                  </>
                ) : (
                  /* FIX: resolve name from companies array; fallback chain matches original */
                  <input
                    type="text"
                    readOnly
                    value={
                      companies.find((c) => c.id === selectedCompany)?.name ||
                      companies.find((c) => c._id === selectedCompany)?.name ||
                      selectedCompany ||
                      "Loading company..."
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 text-gray-700 cursor-not-allowed"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Mobile location/company */}
          <div className="block md:hidden space-y-4">
            <div>
              <label className="text-sm text-gray-600 mb-1 block">
                Location <span className="text-red-500">*</span>
              </label>
              {isUTVMember ? (
                <div className="relative">
                  <button
                    onClick={() => setIsLocationOpen(!isLocationOpen)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md text-left flex justify-between items-center bg-white"
                  >
                    {locations.find((l) => l._id === selectedLocation)?.name || "Select Location"}
                    <svg className={`h-4 w-4 transform transition-transform ${isLocationOpen ? "rotate-180" : ""}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                  {isLocationOpen && (
                    <div className="absolute z-50 mt-1 w-full bg-white shadow-lg rounded-md py-1 max-h-48 overflow-auto border border-gray-300">
                      {locations.map((location, index) => (
                        <button
                          key={index}
                          onClick={() => { setSelectedLocation(location._id); setIsLocationOpen(false); }}
                          className={`block w-full text-left px-4 py-2 text-sm hover:bg-indigo-100 ${selectedLocation === location._id ? "bg-indigo-50 text-indigo-700" : "text-gray-700"}`}
                        >
                          {location.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <input
                  type="text"
                  readOnly
                  value={locations.find((l) => l._id === selectedLocation)?.name || ""}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 text-gray-700"
                />
              )}
            </div>
            <div>
              <label className="text-sm text-gray-600 mb-1 block">
                Company <span className="text-red-500">*</span>
              </label>
              {isUTVMember ? (
                <div className="relative">
                  <button
                    onClick={() => { if (selectedLocation && !loadingCompanies) setIsCompanyOpen(!isCompanyOpen); }}
                    disabled={!selectedLocation || loadingCompanies}
                    className={`w-full px-3 py-2 text-sm border border-gray-300 rounded-md text-left flex justify-between items-center ${!selectedLocation || loadingCompanies ? "bg-gray-100 cursor-not-allowed text-gray-400" : "bg-white"}`}
                  >
                    {loadingCompanies
                      ? <span className="flex items-center"><Loader2 className="h-3 w-3 animate-spin mr-2" />Loading...</span>
                      : selectedCompany
                        ? companies.find((c) => c._id === selectedCompany)?.name || "Select Company"
                        : "Select Company"}
                    <svg className={`h-4 w-4 transform transition-transform ${isCompanyOpen ? "rotate-180" : ""}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                  {isCompanyOpen && selectedLocation && !loadingCompanies && (
                    <div className="absolute z-50 mt-1 w-full bg-white shadow-lg rounded-md py-1 max-h-48 overflow-auto border border-gray-300">
                      {companies.length > 0 ? companies.map((company) => (
                        <button
                          key={company._id}
                          onClick={() => { setSelectedCompany(company._id); setIsCompanyOpen(false); }}
                          className={`block w-full text-left px-4 py-2 text-sm hover:bg-indigo-100 ${selectedCompany === company._id ? "bg-indigo-50 text-indigo-700" : "text-gray-700"}`}
                        >
                          {company.name}
                        </button>
                      )) : <div className="px-4 py-2 text-sm text-gray-500">No companies found for this location</div>}
                    </div>
                  )}
                </div>
              ) : (
                <input
                  type="text"
                  readOnly
                  value={
                    companies.find((c) => c._id === selectedCompany)?.name ||
                    selectedCompany ||
                    "Loading company..."
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 text-gray-700"
                />
              )}
            </div>
          </div>
        </div>

        {/* ── Filters (only when company selected) ── */}
        {selectedCompany && (
          <div className="mb-4">
            {/* Desktop */}
            <div className="hidden md:flex gap-2 mb-2">
              <div className="flex-1">
                <label className="text-sm text-gray-600 mb-1 block">From Date <span className="text-red-500">*</span></label>
                <CalendarInput value={fromDate} onChange={(value) => { setFromDate(value); setDateError(""); }} className="px-2 py-1.5" />
              </div>
              <div className="flex-1">
                <label className="text-sm text-gray-600 mb-1 block">To Date <span className="text-red-500">*</span></label>
                <CalendarInput value={toDate} onChange={(value) => { setToDate(value); setDateError(""); }} className="px-2 py-1.5" />
              </div>
              <div className="flex-1 relative">
                <label className="text-sm text-gray-600 mb-1 block">Select Department</label>
                <div className="relative" ref={departmentDropdownRef1}>
                  <button
                    onClick={toggleDepartmentDropdown1}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md text-left flex justify-between items-center"
                  >
                    {selectedDepartments.length === 0 ? "Select Department"
                      : selectedDepartments.length === departments.length ? "All Departments"
                      : selectedDepartments.length > 2 ? `${selectedDepartments.length} selected`
                      : selectedDepartments.join(", ")}
                    <svg className={`h-4 w-4 transform transition-transform ${isDeptOpen11 ? "rotate-180" : ""}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                  {isDeptOpen11 && (
                    <div className="absolute z-[9999] mt-1 w-full bg-white shadow-lg rounded-md py-1 max-h-48 overflow-auto border border-gray-300">
                      <label className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-indigo-100">
                        <input type="checkbox" checked={selectedDepartments.length === departments.length} onChange={handleSelectAllDepartments} className="mr-2" />
                        Select All
                      </label>
                      {alldepartmentnames.map((dept, index) => (
                        <label key={index} className={`flex items-center px-4 py-2 text-sm hover:bg-indigo-100 ${selectedDepartments.includes(dept) ? "bg-indigo-50 text-indigo-700" : "text-gray-700"}`}>
                          <input type="checkbox" checked={selectedDepartments.includes(dept)} onChange={() => toggleDepartment(dept)} className="mr-2" />
                          {dept}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-end">
                <button
                  onClick={handleApply}
                  disabled={!fromDate || !toDate || loading}
                  className="px-4 py-1.5 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 disabled:bg-indigo-300 disabled:cursor-not-allowed flex items-center justify-center gap-1"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <span>Apply</span>}
                </button>
              </div>
            </div>

            <div className="flex gap-5">
              <div className="hidden md:flex justify-start">
                {hasFeatureAccess("Mark Attendance") && (
                  <button
                    onClick={() => { setSelectedRecord(false); setShowEntryModal(true); }}
                    className="px-4 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 flex items-center justify-center gap-1"
                  >
                    <LogIn className="h-4 w-4" />
                    <span>Mark Entry</span>
                  </button>
                )}
              </div>
              <div className="hidden md:flex justify-start">
                {hasFeatureAccess("Mark Attendance") && (
                  <button
                    onClick={() => { setAttendanceFilePage(true); setshowuploadfilemodal(true); setSuccess(false); }}
                    className="px-4 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 flex items-center justify-center gap-1"
                  >
                    Upload csv/excel file
                  </button>
                )}
              </div>
            </div>

            {/* Mobile */}
            <div className="block md:hidden">
              <div className="grid grid-cols-2 gap-2 mb-2">
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">From Date<span className="text-red-500">*</span></label>
                  <input
                    type="date" value={fromDate}
                    onChange={(e) => { setFromDate(e.target.value); setDateError(""); }}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md cursor-pointer"
                    onClick={(e) => toggleCalendar("fromDate", e)}
                    onBlur={() => handleCalendarBlur("fromDate")}
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">To Date<span className="text-red-500">*</span></label>
                  <input
                    type="date" value={toDate}
                    onChange={(e) => { setToDate(e.target.value); setDateError(""); }}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md cursor-pointer"
                    onClick={(e) => toggleCalendar("toDate", e)}
                    onBlur={() => handleCalendarBlur("toDate")}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <div className="relative">
                  <label className="text-sm text-gray-600 mb-1 block">Select Department</label>
                  <div className="relative" ref={departmentDropdownRef2}>
                    <button onClick={() => setIsDeptOpen12(!isDeptOpen12)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md text-left flex justify-between items-center">
                      {selectedDepartments.length === 0 ? "Select Department"
                        : selectedDepartments.length === departments.length ? "All Departments"
                        : selectedDepartments.length > 2 ? `${selectedDepartments.length} selected`
                        : selectedDepartments.join(", ")}
                      <svg className={`h-4 w-4 transform transition-transform ${isDeptOpen12 ? "rotate-180" : ""}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                    {isDeptOpen12 && (
                      <div className="absolute z-[9999] mt-1 w-full bg-white shadow-lg rounded-md py-1 max-h-48 overflow-auto border border-gray-300">
                        <label className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-indigo-100">
                          <input type="checkbox" checked={selectedDepartments.length === departments.length} onChange={handleSelectAllDepartments} className="mr-2" />
                          Select All
                        </label>
                        {departments.map((dept, index) => (
                          <label key={index} className={`flex items-center px-4 py-2 text-sm hover:bg-indigo-100 ${selectedDepartments.includes(dept) ? "bg-indigo-50 text-indigo-700" : "text-gray-700"}`}>
                            <input type="checkbox" checked={selectedDepartments.includes(dept)} onChange={() => toggleDepartment(dept)} className="mr-2" />
                            {dept}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="relative">
                  <label className="text-sm text-gray-600 mb-1 block">Select Payment Status</label>
                  <div className="relative" ref={paymentDropdownRef2}>
                    <button onClick={() => setIsPaymentOpen12(!isPaymentOpen12)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md text-left flex justify-between items-center">
                      {selectedPaymentStatuses.length === 0 ? "Select Payment Status"
                        : selectedPaymentStatuses.length === paymentOptions.length ? "All Statuses"
                        : selectedPaymentStatuses.length > 2 ? `${selectedPaymentStatuses.length} selected`
                        : selectedPaymentStatuses.join(", ")}
                      <svg className={`h-4 w-4 transform transition-transform ${isPaymentOpen12 ? "rotate-180" : ""}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                    {isPaymentOpen12 && (
                      <div className="absolute z-[9999] mt-1 w-full bg-white shadow-lg rounded-md py-1 max-h-48 overflow-auto border border-gray-300">
                        <label className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-indigo-100">
                          <input type="checkbox" checked={selectedPaymentStatuses.length === paymentOptions.length} onChange={handleSelectAllPayments} className="mr-2" />
                          Select All
                        </label>
                        {paymentOptions.map((status, index) => (
                          <label key={index} className={`flex items-center px-4 py-2 text-sm hover:bg-indigo-100 ${selectedPaymentStatuses.includes(status) ? "bg-indigo-50 text-indigo-700" : "text-gray-700"}`}>
                            <input type="checkbox" checked={selectedPaymentStatuses.includes(status)} onChange={() => togglePaymentStatus(status)} className="mr-2" />
                            {status}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex justify-between">
                <button
                  onClick={() => { setSelectedRecord(null); setShowEntryModal(true); }}
                  className="px-4 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 flex items-center justify-center gap-1"
                >
                  <span>Mark Entry</span>
                </button>
                {hasFeatureAccess("Mark Attendance") && (
                  <button
                    onClick={() => { setAttendanceFilePage(true); setshowuploadfilemodal(true); setSuccess(false); }}
                    className="px-4 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 flex items-center justify-center gap-1"
                  >
                    Upload csv/excel file
                  </button>
                )}
                <button
                  onClick={handleApply}
                  disabled={!fromDate || !toDate || loading}
                  className="px-4 py-1.5 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 disabled:bg-indigo-300 disabled:cursor-not-allowed flex items-center justify-center gap-1"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <span>Apply</span>}
                </button>
              </div>
            </div>
          </div>
        )}

        {dateError && <div className="text-red-500 text-sm mb-2">{dateError}</div>}

        {selectedCompany && (
          <div className="flex justify-end mb-4">
            <button
              onClick={downloadAttendanceData}
              disabled={!fromDate || !toDate || attendanceDownloadLoading}
              className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed flex items-center justify-center gap-1"
            >
              {attendanceDownloadLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <span>Download Attendance</span>}
            </button>
          </div>
        )}

        {fetchError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 mr-2" />
              <span className="text-sm">{fetchError}</span>
            </div>
          </div>
        )}

        {/* ── Table ── */}
        {showTable && !fetchError && (
          <div className="w-full">
            <div className="max-h-[70vh] overflow-y-auto overflow-x-auto relative">
              <table className="w-full text-sm border-collapse">
                <thead className="sticky top-0 bg-white shadow-md z-[50] overflow-visible">
                  <tr className="bg-gray-100">
                    <th className="p-2 text-left">Name <br />(Worker Code)</th>
                    <th className="p-2 text-center">Department</th>
                    <th className="p-2 text-center">Entry Date <br />& Time</th>
                    <th className="p-2 text-center">Exit Date <br />& Time</th>
                    <th className="p-2 text-center">Payment <br />(Working Hr.)</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="5" className="text-center p-4"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></td></tr>
                  ) : attendanceData.length > 0 ? (
                    attendanceData.map((record) => (
                      <tr key={record.attendanceId} className="border-b hover:bg-gray-50 cursor-pointer">
                        {/* Name */}
                        {/* Name */}
<td className="p-2 text-left min-w-[140px]" onClick={() => handleRowClick(record.userId)}>
  <div className="flex items-center">
    {record.serialNumber && <div className="mr-3">{record.serialNumber}.</div>}

    {/* Profile photo */}
    <div className="relative w-12 h-12 flex-shrink-0">
      {record.profilePhotoUrl && imageLoadingStates[record.profilePhotoUrl] && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-200 rounded-full">
          <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
        </div>
      )}
      <img
        src={record.profilePhotoUrl || defaultProfile}
        alt="Profile"
        className={`w-12 h-12 rounded-full object-fill cursor-pointer border border-gray-300 hover:opacity-80 transition-opacity ${
          record.profilePhotoUrl && imageLoadingStates[record.profilePhotoUrl] ? "opacity-0" : "opacity-100"
        }`}
        onClick={(e) => { e.stopPropagation(); openPhotoModal(record.profilePhotoUrl || defaultProfile); }}
        onLoad={(e) => { if (e.target.complete && record.profilePhotoUrl) handleImageLoad(record.profilePhotoUrl); }}
        onError={(e) => {
          e.target.src = defaultProfile;
          if (record.profilePhotoUrl) handleImageLoad(record.profilePhotoUrl);
        }}
      />
    </div>

    {/* Name + Verify Icon + Bank Alert + Call */}
    <div className="ml-2 flex items-start gap-1">

      {/* group/name: name & workerCode — phone tooltip */}
      <div className="relative group/name">
        <div className="font-medium leading-tight">{record.name || "N/A"}</div>
        <div className="text-xs font-semibold text-gray-700 leading-tight mt-0.5">{record.workerCode || "N/A"}</div>

        {/* Phone tooltip */}
        {/* <div className="absolute bottom-full left-0 mb-2 z-[9999] hidden group-hover/name:block bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap pointer-events-none">
           {record.phone || "Phone No. not exist"}
          <div className="absolute top-full left-4 border-4 border-transparent border-t-gray-800" />
        </div> */}
      </div>

      {/* group/verify: verify icon aligned to name row top */}
<div className="relative group/verify mt-0.5">
  {getAccountVerificationIcon(record.nameVerificationStatus).icon}

  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 z-50 hidden group-hover/verify:block">
    <div className="bg-gray-800 text-white rounded-md shadow-lg px-2.5 py-1.5 w-[180px]">

      {/* Status */}
      <div className="text-[11px] font-semibold leading-tight">
        {record.nameVerificationStatus === "failed"
          ? "Account Not Verified"
          : getAccountVerificationIcon(record.nameVerificationStatus).tooltip}
      </div>

      {/* Reason */}
      {record.nameVerificationStatus === "failed" &&
        record.nameVerificationRejectionReason && (
          <div className="mt-1 text-[10px] leading-tight text-gray-300">
            <span className="text-gray-400">Reason:</span>{" "}
            <span className="break-words">
              {record.nameVerificationRejectionReason}
            </span>
          </div>
        )}
    </div>

    {/* Arrow */}
    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
  </div>
</div>

      {/* Bank account alert aligned to name row top */}
      {record?.haveAccount === false && (
        <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" title="Bank account not added" />
      )}

      {/* group/call: call icon — opens dialpad with number prefilled */}
      {record.phone && (
        <div className="relative group/call mt-0.5">
          <Phone
            className="w-4 h-4 text-green-600 hover:text-green-700 cursor-pointer transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              const cleanNumber = record.phone.replace(/[^\d+]/g, "");
              window.location.href = `tel:${cleanNumber}`;
            }}
          />
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 hidden group-hover/call:block bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap pointer-events-none">
            {record.phone}
            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
          </div>
        </div>
      )}

    </div>
  </div>
</td>
                        {/* Department */}
                        <td className="p-2 text-center">{record.department ? record.department : "_"}</td>

                        {/* Entry */}
                        <td className="p-2 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <div className="flex flex-col items-center">
                              <div className="flex items-center justify-center gap-1">
                                <div className="font-medium">{formatDateToDisplay(record.entryDate)}</div>
                                {record.busEntry && <Bus className="h-4 w-4 text-[#3D5A80]" title="Used bus service for entry" />}
                              </div>
                              <div className="flex items-center justify-center gap-1">
                                <span className="text-xs text-gray-600">{formatTimeToIST(record.entryTime)}</span>
                              </div>
                            </div>
                            {record.entryTime &&
                              (record.PaymentStatus?.toLowerCase() === "pending" || record.PaymentStatus?.toLowerCase() === "on-hold") &&
                              hasFeatureAccess("Modify Attendance") && (
                                <Edit className="h-4 w-4 text-black cursor-pointer hover:text-gray-700"
                                  onClick={(e) => handleEntryClick(record, e)} title="Modify Entry Time" />
                              )}
                          </div>
                        </td>

                        {/* Exit */}
<td className="p-2 text-center">
  <div className="flex items-center justify-center gap-2">
    <div className="flex flex-col items-center">
      <div className="flex items-center justify-center gap-1">
        <div className="font-medium">{formatDateToDisplay(record.exitDate)}</div>
        {record.exitTime && record.busExit && (
          <Bus className="h-4 w-4 text-[#3D5A80]" title="Used bus service for exit" />
        )}
      </div>

      <div className="flex items-center justify-center gap-1">
        {record.exitTime ? (
          <>
            <span className="text-xs text-gray-600">{formatTimeToIST(record.exitTime)}</span>

            {record.haveFood && (
              <UtensilsCrossed className="h-4 w-4 text-[#F4A261]" title="Availed food service" />
            )}

            {record.accessories && record.accessories.length > 0 &&
              record.accessories.map((acc) => (
                <img
                  key={acc.accessoryId}
                  src={acc.logo}
                  alt={acc.name}
                  className="h-4 w-4 object-contain"
                  title={acc.price !== undefined ? `${acc.name} - ₹${acc.price}` : acc.name}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = "/fallback-accessory-icon.png"; // optional fallback
                  }}
                />
              ))}
          </>
        ) : (
          hasFeatureAccess("Mark Attendance") && (
            <LogOut
              className="h-4 w-4 text-black cursor-pointer hover:text-gray-700"
              onClick={(e) => handleExitClick(record, e)}
              title="Mark Exit Time"
            />
          )
        )}
      </div>
    </div>

    {record.exitTime &&
      (record.PaymentStatus?.toLowerCase() === "pending" || record.PaymentStatus?.toLowerCase() === "on-hold") &&
      hasFeatureAccess("Modify Attendance") && (
        <Edit
          className="h-4 w-4 text-black cursor-pointer hover:text-gray-700"
          onClick={(e) => handleExitClick(record, e)}
          title="Modify Exit Time"
        />
      )}
  </div>
</td>

                        {/* Payment */}
<td className="p-2 text-center">
  <div className="flex flex-col items-center gap-1">
    {/* Top line: amount + status/action icons */}
    <div className="flex items-center justify-center w-auto mx-auto relative">
      <span className={`px-2 py-0.5 rounded-full text-xs ${getPaymentStatusColor(record.PaymentStatus)}`}>
        ₹{record.payment?.amount}
      </span>

      {hasFeatureAccess("Send Payout Reports") && hasFeatureAccess("Payout Approver") && (
        <Edit
          className="h-4 w-4 text-black cursor-pointer hover:text-gray-700"
          onClick={(e) => handlePaymentEditClick(record, e)}
          title="Modify Payment Amount"
        />
      )}

      {isUTV === "true" &&
        (record.PaymentStatus?.toLowerCase() === "failed" || record.PaymentStatus?.toLowerCase() === "payoutgenerated") && (
          <div className="ml-1 relative tooltip-trigger" onClick={(e) => handlePending(record.attendanceId, e)}>
            {retryingPayments[record.attendanceId] ? (
              <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 cursor-pointer text-blue-600 hover:text-blue-800 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            )}
          </div>
        )}

      {(record.PaymentStatus?.toLowerCase() === "success" ||
        (record.PaymentStatus?.toLowerCase() === "failed" && !retryingPayments[record.attendanceId])) && (
        <div
          className="ml-1 relative tooltip-trigger"
          onMouseEnter={(e) => { if (!isTooltipPinned || activeTooltipId !== record.attendanceId) showTooltip(e, record, false); }}
          onMouseLeave={hideTooltip}
          onClick={(e) => {
            e.stopPropagation();
            if (activeTooltipId === record.attendanceId && isTooltipPinned) {
              setTooltipVisible(false); setTooltipContent(null);
              setActiveTooltipId(null); setIsTooltipPinned(false);
            } else {
              showTooltip(e, record, true);
            }
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg"
            className={`h-4 w-4 cursor-pointer transition-colors ${activeTooltipId === record.attendanceId && isTooltipPinned ? "text-blue-800" : "text-blue-600 hover:text-blue-800"}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      )}

      {record.WorkerAccepted === "rejected" && (
        <Flag className="w-4 h-4 text-red-500 ml-1 cursor-pointer"
          onMouseEnter={(e) => showWorkerAcceptedTooltip(e, record)}
          onMouseLeave={hideWorkerAcceptedTooltip}
          onClick={(e) => { e.stopPropagation(); showWorkerAcceptedTooltip(e, record); }} />
      )}

      {workerAcceptedTooltip.visible && workerAcceptedTooltip.content && (
        <div
          className="fixed z-[9999] p-3 text-xs bg-white rounded-lg border border-gray-200 max-w-[200px] transition-opacity duration-200"
          style={{ left: workerAcceptedTooltip.position.x, top: workerAcceptedTooltip.position.y, transform: "translate(-50%, -100%)" }}
        >
          <div className="break-words text-gray-800">{workerAcceptedTooltip.content}</div>
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-white"></div>
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-200 mt-[-1px]"></div>
        </div>
      )}
    </div>

    {/* Bottom line: hrs + accessory indicator (generic icon, not the logo) */}
    <div className="flex items-center justify-center gap-1">
      <span className="text-xs text-gray-500">{record?.workingHours || "0.00"} hrs</span>
      {record.payment?.accessories && record.payment.accessories.length > 0 && (
        <div
          className="relative tooltip-trigger"
          onMouseEnter={(e) => showAccessoryTooltip(e, record)}
          onMouseLeave={hideAccessoryTooltip}
          onClick={(e) => { e.stopPropagation(); showAccessoryTooltip(e, record); }}
        >
          <Package className="h-4 w-4 text-gray-500 hover:text-gray-700 cursor-pointer" />
        </div>
      )}
    </div>

    {accessoryTooltip.visible && accessoryTooltip.content && (
      <div
        className="fixed z-[9999] p-3 text-xs bg-white rounded-lg border border-gray-200 max-w-[220px]"
        style={{
          left: accessoryTooltip.position.x,
          top: accessoryTooltip.position.y,
          transform: "translate(-50%, -100%)",
        }}
      >
        <div className="space-y-1.5">
          {accessoryTooltip.content.accessories.map((acc) => (
            <div key={acc.accessoryId} className="flex items-center gap-2">
              <img
                src={acc.logo}
                alt={acc.name}
                className="h-4 w-4 rounded-full"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = "/fallback-accessory-icon.png";
                }}
              />
              <span className="text-gray-800">{acc.name}</span>
            </div>
          ))}
<div className="pt-1 border-t border-gray-100 font-medium text-gray-900">
  {accessoryTooltip.content.totalDeduction === 0
    ? `Total Refund Amount: ₹${accessoryTooltip.content.totalPromotion ?? 0}`
    : `Total Deduction: ₹${accessoryTooltip.content.totalDeduction}`}
</div>
        </div>
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-white"></div>
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-200 mt-[-1px]"></div>
      </div>
    )}
  </div>
</td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="5" className="text-center p-4 text-gray-500">No records found</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage} totalPages={totalPages}
                onPageChange={handlePageChange} totalItems={totalItems}
                activeColor="#4f46e5" inactiveColor="#f1f5f9" textColor="#4f46e5"
                containerStyle={{ borderTop: "1px solid #e5e7eb", marginTop: "0" }}
              />
            )}
          </div>
        )}

        {/* ── Photo Modal ── */}
        {showPhotoModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[1000] p-4">
            <div className="relative max-w-md">
              <button onClick={closePhotoModal} className="absolute -top-4 -right-4 bg-white rounded-full p-2 hover:bg-gray-100 z-10">
                <X size={24} className="text-gray-600" />
              </button>
              <div className="relative">
                {isModalImageLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-200 rounded-lg">
                    <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
                  </div>
                )}
                <img
                  src={selectedPhoto} alt="Profile"
                  className={`w-full h-auto rounded-lg max-h-[80vh] object-fill ${isModalImageLoading ? "opacity-0" : "opacity-100"}`}
                  onLoad={() => setIsModalImageLoading(false)}
                  onError={() => setIsModalImageLoading(false)}
                />
              </div>
            </div>
          </div>
        )}

        {/* ── Attendance File Upload ── */}
        {attendancefilepage && (
          <AttendanceFile
            departments={alldepartmentnames}
            showuploadfilemodal={showuploadfilemodal}
            setshowuploadfilemodal={setshowuploadfilemodal}
            Success={Success}
            setSuccess={setSuccess}
            selectedCompany={selectedCompany}
          />
        )}

        {/* ── Payment ref tooltip ── */}
        {tooltipVisible && tooltipContent && (
          <div
            className={`fixed z-[9999] mr-4 p-3 text-xs bg-white shadow-xl rounded-lg border border-gray-200 max-w-[170px] transition-opacity duration-200 ${isTooltipPinned ? "pointer-events-auto" : "pointer-events-none"}`}
            style={{ left: tooltipPosition.x, top: tooltipPosition.y, transform: "translate(-50%, -100%)" }}
          >
            <div className="break-words">
              <strong className="text-gray-800">Reference ID :</strong>
              <span className="text-gray-600 ml-1">{tooltipContent.referenceId}</span>
            </div>
            <div className="break-words mt-2">
              <strong className="text-gray-800">Remarks :</strong>
              <span className="text-gray-600 ml-1">{tooltipContent.remarks}</span>
            </div>
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-white"></div>
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-200 mt-[-1px]"></div>
          </div>
        )}

        {/* ── HandleEntry ── */}
        <HandleEntry
          showEntryModal={showEntryModal}
          setShowEntryModal={setShowEntryModal}
          selectedRecord={selectedRecord}
          selectedCompany={selectedCompany}
          currentPage={currentPage}
          fetchAttendanceData={fetchAttendanceData}
          showentrymarkdays={showentrymarkdays}
          setshowentrymarkdays={setshowentrymarkdays}
        />

        {/* ── HandleExit ── */}
        <HandleExit
          showExitModal={showExitModal}
          setShowExitModal={setShowExitModal}
          selectedRecord={selectedRecord}
          selectedCompany={selectedCompany}
          isUTVMember={isUTVMember}
          currentPage={currentPage}
          fetchAttendanceData={fetchAttendanceData}
        />

        {/* ── HandlePayment ── */}
        <HandlePayment
          paymentEditModal={paymentEditModal}
          setPaymentEditModal={setPaymentEditModal}
          showConfirmationModal={showConfirmationModal}
          setShowConfirmationModal={setShowConfirmationModal}
          selectedAttendanceId={selectedAttendanceId}
          setSelectedAttendanceId={setSelectedAttendanceId}
          retryingPayments={retryingPayments}
          setRetryingPayments={setRetryingPayments}
          currentPage={currentPage}
          fetchAttendanceData={fetchAttendanceData}
          setAttendanceData={setAttendanceData}
        />
      </div>
    </div>
  );
};

export default ViewAttendance;